'use client';

import { createContext, useContext, useMemo, ReactNode, useEffect } from 'react';
import { create, useStore } from 'zustand';
import { useUser } from '@clerk/nextjs';

// -------------------------------------
// Types
// -------------------------------------

export interface ClerkAuthState {
  user: ReturnType<typeof useUser>['user'] | null;
  isLoaded: boolean;
  isSignedIn: boolean;
}

export type ClerkAuthStoreState = ClerkAuthState;

// -------------------------------------
// Store Factory
// -------------------------------------

export const createClerkAuthStore = () =>
  create<ClerkAuthStoreState>(set => ({
    user: null,
    isLoaded: false,
    isSignedIn: false,
  }));

// -------------------------------------
// Context Setup
// -------------------------------------

const fallbackClerkAuthStore = createClerkAuthStore();
const ClerkAuthStoreContext =
  createContext<ReturnType<typeof createClerkAuthStore>>(fallbackClerkAuthStore);

export const ClerkAuthStoreProvider = ({ children }: { children: ReactNode }) => {
  const { user, isLoaded, isSignedIn } = useUser();

  const store = useMemo(() => createClerkAuthStore(), []);

  useEffect(() => {
    store.setState({ user, isLoaded, isSignedIn });
  }, [user, isLoaded, isSignedIn]);

  return <ClerkAuthStoreContext.Provider value={store}>{children}</ClerkAuthStoreContext.Provider>;
};

// -------------------------------------
// Hook
// -------------------------------------

export function useClerkAuthStore<T>(selector: (state: ClerkAuthStoreState) => T): T {
  const store = useContext(ClerkAuthStoreContext);
  return useStore(store, selector);
}
